import { useRef, useEffect, useMemo, useCallback, createContext, useContext, type ReactNode } from 'react'
import { HeadlessModal } from '@inertiaui/modal-react'
import { router } from '@inertiajs/react'
import { Sheet, SheetContent } from '@atoms/Sheet'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import clsx from 'clsx'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('shared')

export type ModalReloadOptions = {
  only?: string[]
}

type ModalSheetContextValue = {
  close: () => void
  reload: (options?: ModalReloadOptions) => void
}

const ModalSheetContext = createContext<ModalSheetContextValue | null>(null)

export function useModalSheetContext() {
  return useContext(ModalSheetContext)
}

export const SHEET_CLOSE_ANIMATION_MS = 350

export const BASE_RELOAD_TIMEOUT_MS = 2000

function reloadBasePageOnNextNavigate(only: string[]) {
  let settled = false
  let stop = () => {}

  const watchdog = setTimeout(() => {
    settled = true
    stop()
  }, BASE_RELOAD_TIMEOUT_MS)

  stop = router.on('navigate', () => {
    if (settled) return
    settled = true
    clearTimeout(watchdog)
    stop()
    router.reload({ only })
  })
}

type ModalSheetRootProps = {
  children: ReactNode
  /** Props of the page behind the sheet to refetch once the sheet has closed. */
  reloadOnClose?: string[]
}

// Dialogs and TinyMCE surfaces stacked on top of the sheet render into their own
// portals (document.body), which sit outside the sheet's content in the DOM. Radix
// therefore treats interactions and Escape presses that belong to those layers as
// happening "outside" the sheet and dismisses it — taking any nested dialog down
// with it. These helpers let the sheet recognise and ignore those events.
const NESTED_LAYER_SELECTOR = '.tox-tinymce-aux, .tox-dialog, .tox-silver-sink, [role="dialog"]'

type ModalSheetPanelProps = ModalSheetRootProps & {
  close: () => void
  reload: (options?: ModalReloadOptions) => void
  isOpen: boolean
  afterLeave: () => void
}

function ModalSheetPanel({ children, close, reload, isOpen, afterLeave, reloadOnClose }: ModalSheetPanelProps) {
  const afterLeaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => () => clearTimeout(afterLeaveTimer.current), [])

  const dismiss = useCallback(() => {
    close()
    clearTimeout(afterLeaveTimer.current)
    afterLeaveTimer.current = setTimeout(() => {
      if (reloadOnClose?.length) reloadBasePageOnNextNavigate(reloadOnClose)
      afterLeave()
    }, SHEET_CLOSE_ANIMATION_MS)
  }, [close, afterLeave, reloadOnClose])

  const contextValue = useMemo(() => ({ close: dismiss, reload }), [dismiss, reload])

  const belongsToNestedLayer = (el: Element | null) => {
    const layer = el?.closest(NESTED_LAYER_SELECTOR)
    return !!layer && layer !== contentRef.current && !contentRef.current?.contains(layer)
  }

  const hasNestedLayerOpen = () =>
    Array.from(document.querySelectorAll(NESTED_LAYER_SELECTOR)).some(
      (el) => el !== contentRef.current && !contentRef.current?.contains(el)
    )

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) dismiss()
      }}
    >
      <SheetContent
        ref={contentRef}
        portalContainer={document.getElementById('v2-root')}
        onInteractOutside={(e) => {
          if (belongsToNestedLayer(e.target as Element | null)) e.preventDefault()
        }}
        onEscapeKeyDown={(e) => {
          if (hasNestedLayerOpen()) e.preventDefault()
        }}
      >
        <ModalSheetContext.Provider value={contextValue}>{children}</ModalSheetContext.Provider>
      </SheetContent>
    </Sheet>
  )
}

function ModalSheetRoot({ children, reloadOnClose }: ModalSheetRootProps) {
  return (
    <HeadlessModal>
      {({ close, reload, isOpen, afterLeave }) => (
        <ModalSheetPanel
          close={close}
          reload={reload}
          isOpen={isOpen}
          afterLeave={afterLeave}
          reloadOnClose={reloadOnClose}
        >
          {children}
        </ModalSheetPanel>
      )}
    </HeadlessModal>
  )
}

type ModalSheetHeaderProps = {
  title: ReactNode
}

function ModalSheetHeader({ title }: ModalSheetHeaderProps) {
  const ctx = useModalSheetContext()
  if (!ctx) throw new Error('ModalSheet.Header must be used within ModalSheet.Root')
  const { close } = ctx

  return (
    <header className="tw:border-b tw:border-gray-100 tw:py-5 tw:px-6 tw:bg-linear-to-b tw:from-primary-700 tw:to-primary-600">
      <div className="tw:flex tw:items-center tw:justify-between">
        <DialogPrimitive.Title asChild>
          <h2 className="tw:text-xl tw:font-bold tw:text-white">{title}</h2>
        </DialogPrimitive.Title>
        <button
          onClick={close}
          aria-label={t('buttons.close')}
          className={clsx(
            'tw:rounded tw:p-1',
            'tw:text-white/80 tw:hover:text-white tw:hover:bg-white/10',
            'tw:transition-colors'
          )}
        >
          <Icon type="x-close" width={20} height={20} />
        </button>
      </div>
    </header>
  )
}

type ModalSheetBodyProps = {
  children: ReactNode
  className?: string
}

function ModalSheetBody({ children, className }: ModalSheetBodyProps) {
  return <div className={clsx('tw:flex tw:flex-col tw:gap-6 tw:p-4', className)}>{children}</div>
}

type ModalSheetFooterProps = {
  children: ReactNode
  className?: string
}

function ModalSheetFooter({ children, className }: ModalSheetFooterProps) {
  return (
    <footer
      className={clsx(
        'tw:sticky tw:bottom-0 tw:w-full tw:px-6 tw:py-4',
        'tw:border-t tw:border-gray-100 tw:bg-white',
        className
      )}
    >
      {children}
    </footer>
  )
}

export const ModalSheet = {
  Root: ModalSheetRoot,
  Header: ModalSheetHeader,
  Body: ModalSheetBody,
  Footer: ModalSheetFooter,
}
